import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import emailApi from "../../api/emailApi";
import { useNavigate } from "react-router-dom";
import ListDisplayTable from "../../components/layout/dataTable/ListDisplayTable";
import { formatEnum } from "../../utils/toTitleCase";
import { getStatusColor } from "../../utils/colors";
import { Eye, Trash2, ToggleLeft, ToggleRight, Calendar, Clock, Plus } from "lucide-react";
import Modal from "../../components/ui/Modal";
import { toast } from "react-toastify";

const EmailSchedules = ({ accountId: propAccountId = null }) => {
  const token = useSelector((s) => s.auth.access_token);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isStandalone = !propAccountId;

  const handlePreview = async (row) => {
    try {
      setPreviewLoading(true);
      const payload = {
        accountId: row.accountId,
        templateId: row.emailTemplateId,
      };
      const res = await emailApi.previewEmail(payload, token);
      setPreviewData(res);
      setPreviewOpen(true);
    } catch (err) {
      console.error("Preview error:", err);
      toast.error("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchSchedules = async () => {
    if (!token) return;
    try {
      setLoading(true);
      let res;
      if (propAccountId) {
        res = await emailApi.getSchedulesByAccount(propAccountId, token);
      } else {
        res = await emailApi.getAllSchedules(token);
      }
      setData(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSchedules();
    }
  }, [token, propAccountId]);

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) return;
    try {
      await emailApi.deleteSchedule(row.id, token);
      toast.success("Email Schedule deleted successfully!");
      fetchSchedules();
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  const handleToggle = async (row) => {
    try {
      await emailApi.toggleSchedule(row.id, token);
      toast.success(row.isEnabled ? "Schedule paused" : "Schedule activated");
      fetchSchedules();
    } catch (error) {
      toast.error("Failed to toggle schedule");
    }
  };

  const filteredData = searchTerm
    ? data.filter(
        (d) =>
          (d.accountName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (d.templateName || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : data;

  const columns = [
    ...(isStandalone
      ? [
          {
            key: "accountName",
            title: "Account",
            render: (v) => (
              <span className="font-medium text-slate-800">{v || "-"}</span>
            ),
          },
        ]
      : []),
    {
      key: "templateName",
      title: "Template",
      render: (v) => <span className="font-medium">{formatEnum(v)}</span>,
    },
    {
      key: "frequency",
      title: "Frequency",
      render: (v) => (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
          {formatEnum(v)}
        </span>
      ),
    },
    {
      key: "scheduledDay",
      title: "Day",
    },
    {
      key: "scheduledMonth",
      title: "Month",
    },
    {
      key: "isEnabled",
      title: "Status",
      render: (value) => (
        <span
          className={`text-gray-700 rounded px-1 ${getStatusColor(
            value ? "Active" : "Inactive"
          )}`}
        >
          {value ? "Active" : "Paused"}
        </span>
      ),
    },
    {
      key: "action",
      title: "Action",
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleToggle(row)}
            className={`p-1 text-xs rounded ${
              row.isEnabled
                ? "bg-yellow-50 text-yellow-600 hover:bg-yellow-100"
                : "bg-green-50 text-green-600 hover:bg-green-100"
            }`}
            title={row.isEnabled ? "Pause" : "Activate"}
          >
            {row.isEnabled ? (
              <ToggleRight size={18} />
            ) : (
              <ToggleLeft size={18} />
            )}
          </button>
          <button
            onClick={() => handlePreview(row)}
            className="p-1 text-xs rounded bg-blue-50 text-blue-600 hover:bg-blue-100"
            title="Preview"
          >
            <Eye size={18} />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="p-1 text-xs rounded bg-red-50 text-red-600 hover:bg-red-100"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className={isStandalone ? "p-6 space-y-4" : ""}>
      {isStandalone && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Calendar size={24} /> Email Scheduler
            </h1>
            <p className="text-slate-500 mt-1">
              Manage scheduled emails across all accounts
            </p>
          </div>
          <button
            onClick={() => navigate("/create/email/schedules", { state: {} })}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <Plus size={16} /> Create Schedule
          </button>
        </div>
      )}

      {!isStandalone && (
        <div className="flex justify-end items-end mb-2 gap-2">
          <button
            onClick={() =>
              navigate("/create/email/schedules", {
                state: { accountId: propAccountId },
              })
            }
            className="bg-primary hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium transition"
          >
            Create
          </button>
        </div>
      )}

      {isStandalone && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by account or template name..."
            className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div
          className={
            isStandalone
              ? "bg-white rounded-lg shadow-sm border border-slate-200"
              : ""
          }
        >
          <ListDisplayTable
            title={isStandalone ? "" : "Email Schedules"}
            data={filteredData}
            columns={columns}
            sortable
            className="h-full flex flex-col"
            headerClassName="bg-gray-100 text-gray-700 sticky top-0 z-10"
            rowClassName="odd:bg-white even:bg-[#fafcfc] hover:bg-gray-50 border-b border-gray-100"
            cellClassName="px-3 py-1 text-sm text-gray-700"
            tableClassName="min-w-full divide-y divide-gray-200"
            tableWrapperClassName="flex-1 overflow-auto rounded-b-lg"
          />
          {filteredData.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>
                {data.length === 0
                  ? "No schedules created yet"
                  : "No schedules match your search"}
              </p>
              {data.length === 0 && (
                <button
                  onClick={() =>
                    navigate("/create/email/schedules", { state: {} })
                  }
                  className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                >
                  Create First Schedule
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Email Preview"
      >
        {!previewData ? (
          <div className="text-center py-10">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-3 text-sm border rounded-lg p-3 bg-gray-50">
              <div>
                <span className="font-semibold">Account</span>
                <p>{previewData.accountName}</p>
              </div>
              <div>
                <span className="font-semibold">Template</span>
                <p>{formatEnum(previewData.templateName)}</p>
              </div>
              <div>
                <span className="font-semibold">Recipient</span>
                <p>{previewData.recipientEmail}</p>
              </div>
              <div>
                <span className="font-semibold">Subject</span>
                <p>{previewData.subject}</p>
              </div>
            </div>
            <div className="lg:col-span-2 border rounded-lg p-4 bg-white whitespace-pre-line text-sm leading-relaxed">
              {previewData.body}
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default EmailSchedules;
